import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Theme, media } from '../theme';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${props => props.theme.background};
  padding: 40px 20px;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  width: 100%;
  min-height: auto;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  ${media.mobile} {
    padding: 20px 16px;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    min-height: auto;
    height: auto;
  }
`;

const Logo = styled.div`
  width: 160px;
  height: 160px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: popPulse 6s ease-in-out infinite;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  
  ${media.mobile} {
    width: 140px;
    height: 140px;
    border-radius: 24px;
  }
  
  svg {
    width: 100%;
    height: 100%;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: scale(1.05) rotate(2deg);
  }
  
  @keyframes popPulse {
    0%, 100% { 
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(247, 147, 26, 0.2);
    }
    50% { 
      transform: scale(1.02);
      box-shadow: 0 0 0 5px rgba(247, 147, 26, 0);
    }
  }
`;

const Title = styled.h1`
  font-size: 56px;
  font-weight: 900;
  background: linear-gradient(135deg, #F7931A, #FFB84D);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 24px;
  letter-spacing: -1px;
  
  ${media.mobile} {
    font-size: 42px;
  }
`;

const Subtitle = styled.h2`
  font-size: 28px;
  color: ${props => props.theme.text};
  margin-bottom: 32px;
  font-weight: 600;
  opacity: 0.9;
  
  ${media.mobile} {
    font-size: 24px;
    margin-bottom: 24px;
  }
`;

const HeroDescription = styled.p`
  color: ${props => props.theme.text};
  font-size: 20px;
  line-height: 1.7;
  margin-bottom: 48px;
  max-width: 700px;
  opacity: 0.8;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  
  ${media.mobile} {
    font-size: 18px;
    margin-bottom: 40px;
  }
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 42px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 24px;
  
  ${media.mobile} {
    font-size: 32px;
  }
`;

const SectionSubtitle = styled.p`
  color: ${props => props.theme.text};
  font-size: 20px;
  text-align: center;
  opacity: 0.8;
  max-width: 600px;
  margin: 0 auto;
  
  ${media.mobile} {
    font-size: 18px;
  }
`;

const HeroSection = styled.div`
  padding: 60px 0;
  width: 100%;
  max-width: 800px;
  
  ${media.mobile} {
    padding: 40px 0;
  }
`;

const MetricsSection = styled.div`
  padding: 80px 0;
  width: 100%;
  background: linear-gradient(135deg, rgba(247, 147, 26, 0.05) 0%, rgba(247, 147, 26, 0.02) 100%);
  border-radius: 24px;
  margin: 40px 0;
  
  ${media.mobile} {
    padding: 60px 20px;
    margin: 30px 0;
  }
`;

const FeaturesSection = styled.div`
  padding: 80px 0;
  width: 100%;
  max-width: 900px;
  
  ${media.mobile} {
    padding: 60px 0;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  margin-top: 60px;
  
  ${media.mobile} {
    grid-template-columns: 1fr;
    gap: 30px;
    margin-top: 40px;
  }
`;

const FeatureCard = styled.div`
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 20px;
  padding: 40px 32px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #F7931A, #FFB84D);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(247, 147, 26, 0.15);
    
    &::before {
      transform: translateX(0);
    }
  }
  
  ${media.mobile} {
    padding: 32px 24px;
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #F7931A, #FFB84D);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 24px;
  color: white;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
`;

const FeatureTitle = styled.h3`
  color: ${props => props.theme.primary};
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 16px;
  
  ${media.mobile} {
    font-size: 20px;
  }
`;

const FeatureDescription = styled.p`
  color: ${props => props.theme.text};
  font-size: 16px;
  line-height: 1.6;
  opacity: 0.9;
  
  ${media.mobile} {
    font-size: 15px;
  }
`;

const CallToActionSection = styled.div`
  padding: 100px 0;
  width: 100%;
  text-align: center;
  background: linear-gradient(135deg, rgba(247, 147, 26, 0.08) 0%, rgba(247, 147, 26, 0.03) 100%);
  border-radius: 32px;
  margin: 60px 0;
  
  ${media.mobile} {
    padding: 80px 20px;
    margin: 40px 0;
  }
`;

const CTATitle = styled.h2`
  font-size: 48px;
  font-weight: 800;
  color: ${props => props.theme.primary};
  margin-bottom: 24px;
  background: linear-gradient(135deg, #F7931A, #FFB84D);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  ${media.mobile} {
    font-size: 36px;
  }
`;

const CTASubtitle = styled.p`
  font-size: 24px;
  color: ${props => props.theme.text};
  margin-bottom: 40px;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  ${media.mobile} {
    font-size: 20px;
    margin-bottom: 32px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 500px;
  margin: 60px auto 0;
  
  ${media.mobile} {
    gap: 20px;
    max-width: 400px;
    margin: 50px auto 0;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.surface};
  border: 2px solid ${props => props.theme.border};
  border-radius: 20px;
  padding: 24px 32px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 90px;
  
  &:hover {
    border-color: #F7931A;
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(247, 147, 26, 0.2);
  }
  
  ${media.mobile} {
    padding: 20px 24px;
    min-height: 80px;
  }
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 900;
  background: linear-gradient(135deg, #F7931A, #FFB84D);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  line-height: 1.2;
  
  ${media.mobile} {
    font-size: 32px;
  }
`;

const StatLabel = styled.div`
  font-size: 16px;
  color: ${props => props.theme.text};
  font-weight: 600;
  opacity: 0.8;
  
  ${media.mobile} {
    font-size: 14px;
  }
`;

const Footer = styled.footer`
  margin-top: 80px;
  padding: 40px 0;
  border-top: 1px solid ${props => props.theme.border};
  text-align: center;
  width: 100%;
  
  ${media.mobile} {
    margin-top: 60px;
    padding: 32px 0;
  }
`;

const GoalSection = styled.div`
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid ${props => props.theme.border};
`;

const GoalTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.theme.primary};
  margin-bottom: 8px;
`;

const GoalText = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.theme.text};
  
  ${media.mobile} {
    font-size: 16px;
  }
`;

const GoalsSection = styled.div`
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 16px;
  padding: 32px;
  margin: 32px auto 0;
  max-width: 500px;
  width: 100%;
  
  ${media.mobile} {
    padding: 24px;
    margin: 24px auto 0;
    max-width: 320px;
  }
`;

const VisionSection = styled.div`
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 16px;
  padding: 32px;
  margin: 32px auto 0;
  max-width: 500px;
  width: 100%;
  
  ${media.mobile} {
    padding: 24px;
    margin: 24px auto 0;
    max-width: 320px;
  }
`;

const PrimaryButton = styled(Link)`
  background: linear-gradient(135deg, #F7931A, #FFB84D);
  color: white;
  padding: 18px 40px;
  border-radius: 50px;
  font-size: 20px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  text-align: center;
  display: inline-block;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(247, 147, 26, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(247, 147, 26, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  ${media.mobile} {
    font-size: 18px;
    padding: 16px 32px;
  }
`;

const ExplanationText = styled.p`
  color: ${props => props.theme.subText};
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 600px;
  
  ${media.mobile} {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

const FooterText = styled.div`
  color: ${props => props.theme.subText};
  font-size: 13px;
  
  ${media.mobile} {
    font-size: 12px;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  
  ${media.mobile} {
    gap: 16px;
  }
`;

const FooterLink = styled(Link)`
  color: ${props => props.theme.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(247, 147, 26, 0.1);
    transform: translateY(-1px);
  }
  
  ${media.mobile} {
    font-size: 12px;
    padding: 4px 8px;
  }
`;

const SocialLink = styled.a`
  color: ${props => props.theme.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(247, 147, 26, 0.1);
    transform: translateY(-1px);
  }
  
  ${media.mobile} {
    font-size: 12px;
    padding: 4px 8px;
  }
`;

const StaticPageLink = styled.a`
  color: ${props => props.theme.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(247, 147, 26, 0.1);
    transform: translateY(-1px);
  }
  
  ${media.mobile} {
    font-size: 12px;
    padding: 4px 8px;
  }
`;

interface IntroPageProps {
  isDarkMode: boolean;
  theme: Theme;
}

const IntroPage: React.FC<IntroPageProps> = ({ isDarkMode, theme }) => {
  const [metrics, setMetrics] = useState({
    totalPredictions: 0,
    averagePrediction: 0,
    predictionSpread: 0
  });
  const [loading, setLoading] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch intro metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        // Use relative URLs - they will automatically use the same protocol (HTTP/HTTPS)
        const response = await fetch('/api/intro/metrics');
        const data = await response.json();
        
        if (data.success) {
          setMetrics(data.data);
        }
      } catch (error) {
        console.error('Error fetching intro metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  // Format number as currency
  const formatCurrency = (number: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(number);
  };

  // Generate backend URLs dynamically
  const getBackendUrl = (path: string) => {
    return `${window.location.protocol}//${window.location.hostname}:3001${path}`;
  };

  return (
    <Container>
      {/* Hero Section */}
      <HeroSection>
        <Logo>
          <img 
            src="/apple-touch-icon-clean.png" 
            alt="ProofOfPrice Logo" 
            width="160" 
            height="160" 
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Logo>
        
        <Title>proofofprice.com</Title>
        <Subtitle>The Truth About Bitcoin Predictions</Subtitle>
        
        <HeroDescription>
          Cut through the noise. Track real predictions from industry experts, influencers, 
          and thought leaders. See who called it right and who missed the mark.
        </HeroDescription>
        
        <PrimaryButton to="/btc">
          Explore Bitcoin Predictions →
        </PrimaryButton>
      </HeroSection>

      {/* Live Metrics Section */}
      {!loading && (
        <MetricsSection>
          <SectionTitle>Live Market Intelligence</SectionTitle>
          <SectionSubtitle>
            Real-time data from the Bitcoin prediction ecosystem
          </SectionSubtitle>
          
          <StatsGrid>
            <StatCard>
              <StatValue>{metrics.totalPredictions}</StatValue>
              <StatLabel>Expert Predictions</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{formatCurrency(metrics.averagePrediction)}</StatValue>
              <StatLabel>Average Target</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{formatCurrency(metrics.predictionSpread)}</StatValue>
              <StatLabel>Price Range</StatLabel>
            </StatCard>
          </StatsGrid>
        </MetricsSection>
      )}

      {/* Features Section */}
      <FeaturesSection>
        <SectionTitle>Why Proof of Price?</SectionTitle>
        <SectionSubtitle>
          The only platform that tracks prediction accuracy with transparency and accountability
        </SectionSubtitle>
        
        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon>📊</FeatureIcon>
            <FeatureTitle>Track Performance</FeatureTitle>
            <FeatureDescription>
              See which experts consistently deliver accurate predictions and which ones are just noise.
              Every prediction is timestamped and verified.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>🎯</FeatureIcon>
            <FeatureTitle>Signal vs Noise</FeatureTitle>
            <FeatureDescription>
              Filter through hand-curated and community-sourced price predictions. 
              Build your reputation by contributing quality forecasts.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>⚡</FeatureIcon>
            <FeatureTitle>Lightning Payments</FeatureTitle>
            <FeatureDescription>
              Submit your Bitcoin predictions and get notified when they appear on our charts. 
              Choose email or LNURL notifications, and support us with value-for-value payments if you find our service useful.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>📈</FeatureIcon>
            <FeatureTitle>Live Visualization</FeatureTitle>
            <FeatureDescription>
              Watch your favorite predictions come to life on interactive charts. 
              Share compelling visualizations with friends and showcase the most accurate forecasters in real-time.
            </FeatureDescription>
          </FeatureCard>
        </FeatureGrid>
      </FeaturesSection>

      {/* Call to Action Section */}
      <CallToActionSection>
        <CTATitle>Find the Fire of Truth</CTATitle>
        <CTASubtitle>
          Join the community building Bitcoin's prediction accuracy database
        </CTASubtitle>
        <PrimaryButton to="/btc">
          Start Exploring →
        </PrimaryButton>
      </CallToActionSection>

      <Footer>
        <FooterContent>
          <FooterText>
            Data collected from public sources. Updated regularly.
          </FooterText>
          <FooterLinks>
            <FooterLink to="/">Home</FooterLink>
            <StaticPageLink href={getBackendUrl('/about')} target="_blank" rel="noopener noreferrer">
              About Us
            </StaticPageLink>
            <StaticPageLink href={getBackendUrl('/terms')} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </StaticPageLink>
            <SocialLink href="https://twitter.com/proofofprice" target="_blank" rel="noopener noreferrer">
              @proofofprice
            </SocialLink>
            <SocialLink href="mailto:proofofprice@protonmail.com">
              Contact Us
            </SocialLink>
            <SocialLink href="https://primal.net/p/nprofile1qqsdrdhnhu76949zjn0zr93992aldfs5h7g2usfkz8kahh5t4pkegug5c9dzr" target="_blank" rel="noopener noreferrer">
              Nostr
            </SocialLink>
          </FooterLinks>
        </FooterContent>
      </Footer>
    </Container>
  );
};

export default IntroPage;